import { TextField } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { useTheme } from "@emotion/react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { MdOutlinePhone } from "react-icons/md";

import { CustomerData } from "./useCustomerDetailsForm";

type Props = {
  customerData: CustomerData;
  handlers: {
    onFirstNameChange: (v: string) => void;
    onLastNameChange: (v: string) => void;
    onPhoneNumberChange: (v: string) => void;
    onEmailChange: (v: string | undefined) => void;
  };
  isCustomerDetailsEditDisabled?: boolean;
  emailError: string | null;
  phoneError: string | null;
};

const telHref = (input: string) => {
  const digits = (input || "").replace(/\D/g, "");
  if (/^0048\d{9}$/.test(digits)) return `tel:+48${digits.slice(-9)}`;
  if (/^48\d{9}$/.test(digits)) return `tel:+48${digits.slice(-9)}`;
  if (/^\d{9}$/.test(digits)) return `tel:${digits}`;
  return `tel:${digits}`;
};

export const CustomerPersonalDetails: React.FC<Props> = ({
  customerData,
  handlers,
  isCustomerDetailsEditDisabled,
  emailError,
  phoneError,
}) => {
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [iconTop, setIconTop] = useState<number>(0);
  const showCallIcon = !phoneError && !!customerData.phoneNumber?.trim();

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const input = wrapper.querySelector("input");
    if (!input) return;
    const update = () => {
      const wr = wrapper.getBoundingClientRect();
      const ir = input.getBoundingClientRect();
      setIconTop(ir.top - wr.top + ir.height / 2);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapper);
    ro.observe(input);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [customerData.phoneNumber, phoneError]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <SectionHeader isHr>
        {customerData.categories.includes("loyaltyUser") ? "Dane osobowe" : "Dane kontaktowe"}
      </SectionHeader>

      <div css={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
        {customerData.categories.includes("loyaltyUser") && (
          <div css={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <TextField
              label="Imię"
              value={customerData.firstName}
              onChange={handlers.onFirstNameChange}
              isDisabled={isCustomerDetailsEditDisabled}
            />
            <TextField
              label="Nazwisko"
              value={customerData.lastName}
              onChange={handlers.onLastNameChange}
              isDisabled={isCustomerDetailsEditDisabled}
            />
          </div>
        )}

        <div ref={wrapperRef} css={{ position: "relative" }}>
          <TextField
            label="Nr telefonu"
            value={customerData.phoneNumber}
            onChange={handlers.onPhoneNumberChange}
            isDisabled={customerData.categories.includes("loyaltyUser")}
            error={phoneError}
          />
          {showCallIcon && (
            <a
              href={telHref(customerData.phoneNumber)}
              css={{
                position: "absolute",
                right: "0.375rem",
                top: iconTop,
                transform: "translateY(-50%)",
                padding: "0.125rem",
                background: "none",
                textDecoration: "none",
                color: theme.colors.primary,
                height: "24px",
              }}
            >
              <MdOutlinePhone size={20} />
            </a>
          )}
        </div>

        <TextField
          label="Email"
          value={customerData.email ?? ""}
          onChange={handlers.onEmailChange}
          isDisabled={isCustomerDetailsEditDisabled}
          error={emailError}
        />
      </div>
    </div>
  );
};
